import React from 'react';
import PropTypes from 'prop-types';
import { LuChevronDown, LuChevronLeft, LuChevronRight, LuCircleAlert, LuFolderOpen, LuMail, LuPhone, LuTreePalm, LuUserMinus } from 'react-icons/lu';
import IconButton from '../../../components/ui/IconButton';
import { COUNT_BADGE, GLASS_CHIP, GLASS_PRESSABLE } from '../../../components/ui/glass';
import useLeaveText from '../../LeaveRequests/parts/leaveText';
import useSmoText from '../smoText';
import WorkerDetails from './WorkerDetails';

const TONES = {
  default: 'tw-text-slate-600 hover:tw-text-slate-900 dark:tw-text-slate-300 dark:hover:tw-text-white',
  alert: 'tw-text-rose-600 hover:tw-text-rose-700 dark:tw-text-rose-300 dark:hover:tw-text-rose-200',
};

/** Compact round button for table rows (the shared IconButton is 40 px). */
const RowButton = ({ label, badge = 0, tone = 'default', onClick, children }) => (
  <button
    type="button"
    title={label}
    aria-label={badge > 0 ? `${label} (${badge})` : label}
    onClick={onClick}
    className={`tw-relative tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-full ${GLASS_CHIP} ${GLASS_PRESSABLE} ${TONES[tone]}`}
  >
    {children}
    {badge > 0 && <span aria-hidden="true" className={COUNT_BADGE}>{badge > 99 ? '99+' : badge}</span>}
  </button>
);

RowButton.propTypes = { label: PropTypes.string.isRequired, badge: PropTypes.number, tone: PropTypes.oneOf(['default', 'alert']), onClick: PropTypes.func.isRequired, children: PropTypes.node.isRequired };

const centersText = (worker) => [...new Set(worker.projects.map((p) => p.centerName).filter(Boolean))].join(', ') || '-';

/** CV/GCP/IATA button; the red count is the number of missing required documents. */
const DocumentsButton = ({ worker, missingDocs, onOpen }) => {
  const { label } = useSmoText();
  const hasMissing = missingDocs.length > 0;
  return (
    <RowButton
      label={hasMissing
        ? label('table.missingDocs', { list: missingDocs.map((d) => d.label).join(', ') })
        : label('table.viewDocs', { name: `${worker.name} ${worker.surname}` })}
      badge={missingDocs.length}
      tone={hasMissing ? 'alert' : 'default'}
      onClick={(e) => {
        // Satır tıklaması projeleri açıyor; belge butonu onu tetiklemesin
        e.stopPropagation();
        onOpen(worker);
      }}
    >
      <LuFolderOpen aria-hidden="true" className="tw-h-4 tw-w-4" />
    </RowButton>
  );
};

DocumentsButton.propTypes = { worker: PropTypes.object.isRequired, missingDocs: PropTypes.array.isRequired, onOpen: PropTypes.func.isRequired };

/** This year's remaining annual leave; opens the worker's leave details. "İzinde" when on leave today. */
const LeaveButton = ({ worker, summary, onOpen }) => {
  const { label } = useSmoText();
  const { days } = useLeaveText();
  const remaining = summary?.remaining ?? null;
  const name = `${worker.name} ${worker.surname}`;
  return (
    <button
      type="button"
      title={[
        label('table.viewLeaves', { name }),
        remaining !== null && label('table.leaveRemaining', { days: days(remaining) }),
        summary?.pendingCount > 0 && label('table.leavePending', { count: summary.pendingCount }),
      ].filter(Boolean).join('\n')}
      aria-label={label('table.viewLeaves', { name })}
      onClick={(e) => {
        // Satır tıklaması projeleri açıyor; izin butonu onu tetiklemesin
        e.stopPropagation();
        onOpen(worker);
      }}
      className={`tw-inline-flex tw-h-8 tw-shrink-0 tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-full tw-px-2.5 tw-text-xs tw-font-semibold tw-tabular-nums ${GLASS_CHIP} ${GLASS_PRESSABLE} ${remaining !== null && remaining <= 0 ? 'tw-text-rose-600 dark:tw-text-rose-300' : 'tw-text-teal-700 dark:tw-text-teal-300'}`}
    >
      <LuTreePalm aria-hidden="true" className="tw-h-4 tw-w-4" />
      {remaining !== null ? days(remaining) : '-'}
      {summary?.current && <span className="tw-rounded-full tw-bg-emerald-500 tw-px-1.5 tw-py-px tw-text-[10px] tw-font-bold tw-text-white">{label('table.onLeave')}</span>}
    </button>
  );
};

LeaveButton.propTypes = { worker: PropTypes.object.isRequired, summary: PropTypes.object, onOpen: PropTypes.func.isRequired };

/** Project count with the unfinished-visit marker next to it. */
const ProjectCount = ({ count, incomplete }) => {
  const { label } = useSmoText();
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5">
      <span className="tw-min-w-[1.75rem] tw-rounded-full tw-bg-sky-500 tw-px-2 tw-py-0.5 tw-text-center tw-text-xs tw-font-bold tw-text-white">{count}</span>
      {/* Satırı açmadan görünen tamamlanmamış vizit belirteci */}
      {incomplete > 0 && (
        <span title={label('table.incompleteVisits', { count: incomplete })} className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-orange-500/15 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-bold tw-text-orange-700 dark:tw-text-orange-300">
          <LuCircleAlert aria-hidden="true" className="tw-h-3 tw-w-3" />
          {incomplete}
        </span>
      )}
    </span>
  );
};

ProjectCount.propTypes = { count: PropTypes.number.isRequired, incomplete: PropTypes.number };

/**
 * The SMO's workers. A row (or card on phones) opens the worker's projects.
 * Table on md+ with left/right scroll buttons; a card list on phones.
 * Bölge Müdürleri Raporları uses it without the documents column
 * (`missingDocsByWorker` null), without unfinished-visit counts and without
 * the leave column (`leaveSummaryByWorker` null).
 */
const WorkersTable = ({
  filteredWorkers, expandedWorkers, toggleWorkerExpand, missingDocsByWorker, incompleteVisitCounts,
  leaveSummaryByWorker, onOpenLeaves,
  canRemoveSMO, onOpenDocuments, onRemoveWorker, tableContainerRef, handleScroll, showLeftScroll, showRightScroll, scrollTable,
  detailsProps,
}) => {
  const { label } = useSmoText();
  const showDocs = !!missingDocsByWorker;
  const showLeaves = !!leaveSummaryByWorker;
  const columnCount = 6 + (showDocs ? 1 : 0) + (showLeaves ? 1 : 0) + (canRemoveSMO ? 1 : 0);

  return (
    <>
      <p className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
        <span className="max-md:tw-hidden">{label('table.rowHint')}</span>
        {showDocs && <span className="tw-inline-flex tw-items-center tw-gap-1"><LuFolderOpen aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />{label('table.docsLegend')}</span>}
        {showLeaves && <span className="tw-inline-flex tw-items-center tw-gap-1"><LuTreePalm aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-teal-500" />{label('table.leaveLegend')}</span>}
        {incompleteVisitCounts && <span className="tw-inline-flex tw-items-center tw-gap-1"><LuCircleAlert aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-orange-500" />{label('table.incompleteLegend')}</span>}
      </p>

      {/* Tablet ve masaüstü */}
      <div className="tw-relative tw-hidden md:tw-block">
        {showLeftScroll && (
          <IconButton label={label('table.scrollLeft')} onClick={() => scrollTable('left')} className="tw-absolute tw-left-2 tw-top-1/2 tw-z-10 -tw-translate-y-1/2">
            <LuChevronLeft aria-hidden="true" className="tw-h-5 tw-w-5" />
          </IconButton>
        )}
        {showRightScroll && (
          <IconButton label={label('table.scrollRight')} onClick={() => scrollTable('right')} className="tw-absolute tw-right-2 tw-top-1/2 tw-z-10 -tw-translate-y-1/2">
            <LuChevronRight aria-hidden="true" className="tw-h-5 tw-w-5" />
          </IconButton>
        )}
        <div ref={tableContainerRef} onScroll={handleScroll} className="tw-relative tw-overflow-x-auto tw-rounded-2xl tw-border tw-border-slate-200/80 glass-scrollbar dark:tw-border-white/10">
          <table className="tw-w-full tw-border-collapse tw-text-sm">
            <thead className="tw-bg-slate-50/90 dark:tw-bg-white/[0.03]">
              <tr>
                <th scope="col" className="tw-w-px tw-py-3 tw-pl-4 tw-pr-1"><span className="tw-sr-only">{label('table.expand')}</span></th>
                <th scope="col" className="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-text-left tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400">{label('table.worker')}</th>
                {/* Belge butonları kendi sütununda: isim uzunluğu ne olursa olsun alt alta hizalı */}
                {showDocs && <th scope="col" className="tw-w-px tw-whitespace-nowrap tw-px-2 tw-py-3 tw-text-center tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400">{label('table.documents')}</th>}
                {showLeaves && <th scope="col" className="tw-w-px tw-whitespace-nowrap tw-px-2 tw-py-3 tw-text-center tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400">{label('table.leave')}</th>}
                {[label('table.email'), label('table.phone'), label('table.projectCount'), label('table.centers')].map((name) => (
                  <th key={name} scope="col" className="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-text-left tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400">{name}</th>
                ))}
                {canRemoveSMO && <th scope="col" className="tw-w-16 tw-px-3 tw-py-3 tw-text-center tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400">{label('table.action')}</th>}
              </tr>
            </thead>
            <tbody className="tw-divide-y tw-divide-slate-200/70 dark:tw-divide-white/[0.06]">
              {filteredWorkers.map((worker) => {
                const expanded = !!expandedWorkers[worker.id];
                const centers = centersText(worker);
                return (
                  <React.Fragment key={worker.id}>
                    <tr
                      onClick={() => toggleWorkerExpand(worker.id)}
                      aria-expanded={expanded}
                      className={`tw-cursor-pointer tw-transition-colors ${expanded ? 'tw-bg-sky-500/[0.06] dark:tw-bg-sky-400/[0.06]' : 'hover:tw-bg-slate-900/[0.02] dark:hover:tw-bg-white/[0.03]'}`}
                    >
                      <td className="tw-py-2.5 tw-pl-4 tw-pr-1 tw-text-slate-400">
                        <LuChevronDown aria-hidden="true" className={`tw-h-4 tw-w-4 tw-transition-transform ${expanded ? 'tw-rotate-180 tw-text-sky-600' : ''}`} />
                      </td>
                      <td className="tw-whitespace-nowrap tw-px-4 tw-py-2.5 tw-font-semibold tw-text-slate-900 dark:tw-text-white">{worker.name} {worker.surname}</td>
                      {showDocs && (
                        <td className="tw-px-2 tw-py-2">
                          <span className="tw-flex tw-justify-center">
                            <DocumentsButton worker={worker} missingDocs={missingDocsByWorker[worker.id] || []} onOpen={onOpenDocuments} />
                          </span>
                        </td>
                      )}
                      {showLeaves && (
                        <td className="tw-px-2 tw-py-2">
                          <span className="tw-flex tw-justify-center">
                            <LeaveButton worker={worker} summary={leaveSummaryByWorker[worker.id]} onOpen={onOpenLeaves} />
                          </span>
                        </td>
                      )}
                      <td className="tw-whitespace-nowrap tw-px-4 tw-py-2.5 tw-text-slate-700 dark:tw-text-slate-200">{worker.email || '-'}</td>
                      <td className="tw-whitespace-nowrap tw-px-4 tw-py-2.5 tw-tabular-nums tw-text-slate-700 dark:tw-text-slate-200">{worker.phone || '-'}</td>
                      <td className="tw-px-4 tw-py-2.5"><ProjectCount count={worker.projects.length} incomplete={incompleteVisitCounts?.byWorker[worker.id]} /></td>
                      {/* Merkez adları uzun; hücre tek satırda kısalır ve tam liste tooltip'te kalır */}
                      <td className="tw-max-w-[22rem] tw-truncate tw-px-4 tw-py-2.5 tw-text-slate-700 dark:tw-text-slate-200" title={centers}>{centers}</td>
                      {canRemoveSMO && (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
                        <td className="tw-px-3 tw-py-2 tw-text-center" onClick={(e) => e.stopPropagation()}>
                          <RowButton label={label('table.removeWorker')} tone="alert" onClick={() => onRemoveWorker(worker)}>
                            <LuUserMinus aria-hidden="true" className="tw-h-4 tw-w-4" />
                          </RowButton>
                        </td>
                      )}
                    </tr>
                    {expanded && (
                      <tr className="tw-bg-slate-50/70 dark:tw-bg-white/[0.02]">
                        <td colSpan={columnCount} className="tw-whitespace-normal tw-px-4 tw-py-4">
                          <WorkerDetails worker={worker} {...detailsProps} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Telefon */}
      <ul className="tw-space-y-2.5 md:tw-hidden">
        {filteredWorkers.map((worker) => {
          const expanded = !!expandedWorkers[worker.id];
          return (
            <li key={worker.id} className={`tw-overflow-hidden tw-rounded-2xl tw-border ${expanded ? 'tw-border-sky-500/40 dark:tw-border-sky-400/30' : 'tw-border-slate-200/80 dark:tw-border-white/10'} tw-bg-white/80 dark:tw-bg-white/[0.03]`}>
              <div className="tw-flex tw-items-start tw-gap-2 tw-p-3.5">
                <button type="button" onClick={() => toggleWorkerExpand(worker.id)} aria-expanded={expanded} className="tw-min-w-0 tw-flex-1 tw-bg-transparent tw-p-0 tw-text-left">
                  <span className="tw-block tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{worker.name} {worker.surname}</span>
                  <span className="tw-mt-1 tw-flex tw-items-center tw-gap-1.5 tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400"><LuMail aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />{worker.email || '-'}</span>
                  <span className="tw-mt-0.5 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-tabular-nums tw-text-slate-500 dark:tw-text-slate-400"><LuPhone aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />{worker.phone || '-'}</span>
                  <span className="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs tw-text-slate-600 dark:tw-text-slate-300">
                    <ProjectCount count={worker.projects.length} incomplete={incompleteVisitCounts?.byWorker[worker.id]} />
                    <span className="tw-min-w-0 tw-break-words">{centersText(worker)}</span>
                  </span>
                </button>
                {showLeaves && <LeaveButton worker={worker} summary={leaveSummaryByWorker[worker.id]} onOpen={onOpenLeaves} />}
                {showDocs && <DocumentsButton worker={worker} missingDocs={missingDocsByWorker[worker.id] || []} onOpen={onOpenDocuments} />}
                {canRemoveSMO && (
                  <RowButton label={label('table.removeWorker')} tone="alert" onClick={() => onRemoveWorker(worker)}>
                    <LuUserMinus aria-hidden="true" className="tw-h-4 tw-w-4" />
                  </RowButton>
                )}
              </div>
              <button
                type="button"
                onClick={() => toggleWorkerExpand(worker.id)}
                aria-expanded={expanded}
                className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-1.5 tw-border-t tw-border-slate-200/70 tw-bg-slate-50/70 tw-py-2 tw-text-xs tw-font-semibold tw-text-sky-700 dark:tw-border-white/10 dark:tw-bg-white/[0.02] dark:tw-text-sky-300"
              >
                {expanded ? label('table.hideProjects') : label('table.showProjects')}
                <LuChevronDown aria-hidden="true" className={`tw-h-4 tw-w-4 tw-transition-transform ${expanded ? 'tw-rotate-180' : ''}`} />
              </button>
              {expanded && (
                <div className="tw-border-t tw-border-slate-200/70 tw-p-3 dark:tw-border-white/10">
                  <WorkerDetails worker={worker} {...detailsProps} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
};

WorkersTable.propTypes = {
  filteredWorkers: PropTypes.array.isRequired,
  expandedWorkers: PropTypes.object.isRequired,
  toggleWorkerExpand: PropTypes.func.isRequired,
  missingDocsByWorker: PropTypes.object,
  leaveSummaryByWorker: PropTypes.object,
  onOpenLeaves: PropTypes.func,
  incompleteVisitCounts: PropTypes.object,
  canRemoveSMO: PropTypes.bool,
  onOpenDocuments: PropTypes.func,
  onRemoveWorker: PropTypes.func,
  tableContainerRef: PropTypes.object.isRequired,
  handleScroll: PropTypes.func.isRequired,
  showLeftScroll: PropTypes.bool,
  showRightScroll: PropTypes.bool,
  scrollTable: PropTypes.func.isRequired,
  detailsProps: PropTypes.object.isRequired,
};

export default WorkersTable;
